import { runInAction } from 'mobx'
import { DocumentAPI } from './API'
import DocumentVersion from './DocumentVersion'
import {
  CreateDocumentVersionPayload,
  DocumentVersionDTO,
} from './dtos/document-version'
import { collection } from '../infra/collection'
import { GetDocument } from './DocumentStore'
import { GetMember, PrimeMember } from '../iam/MemberStore'
import { Id, PrimeProps } from 'types/definitions'
import {
  ClientDataManualVerificationRequiredMessage,
  ClientDataVerifiedMessage,
  VersionScanMessage,
} from './dtos/realtime-messages'
import { CorrectDocumentFieldPayload } from './dtos/document-field'
import { filterNonNil } from 'utils/rx-util'
import { mergeMap, share } from 'rxjs'

export type GetVersionsForDocument = (documentId: Id) => DocumentVersion[]
export type PrimeDocumentVersion = (
  props: PrimeProps<DocumentVersionDTO, 'id'>,
) => DocumentVersion | undefined

export default class DocumentVersionStore {
  versions = collection<DocumentVersionDTO, DocumentVersion>({
    create: () =>
      new DocumentVersion(this.getDocument, this.getMember, this.primeMember),
  })

  clientDataManualVerificationRequired$ = this.api.version
    .when<ClientDataManualVerificationRequiredMessage>(
      'ClientDataManualVerificationRequired',
    )
    .pipe(
      mergeMap(async (e) => {
        await this.refreshVersion(e.data.document_id, e.data.id)
        return this.versions.get(e.data.id)
      }),
      filterNonNil(),
      share(),
    )

  constructor(
    private api: DocumentAPI,
    private getMember: GetMember,
    private primeMember: PrimeMember,
    private getDocument: GetDocument,
  ) {
    this.api.access
      .when<VersionScanMessage>('VersionScanned')
      .subscribe((e) => this.versions.set(e.data))

    this.api.version
      .when<ClientDataVerifiedMessage>('ClientDataVerified')
      .subscribe((e) => this.refreshVersion(e.data.document_id, e.data.id))
  }

  forDocument: GetVersionsForDocument = (id) =>
    this.versions.filter((v) => v.documentId === id)

  async createVersion(data: CreateDocumentVersionPayload) {
    const document = this.getDocument(data.document_id)
    if (!document) {
      throw new Error(
        'Cannot create a version because the document was not found in the collection',
      )
    }
    const result = await this.api.version
      .create(data, {
        onProgress: (e) => {
          document.set({
            uploadProgress: {
              total: e.total,
              uploaded: e.loaded,
              percent: Math.ceil((e.loaded / e.total) * 100),
            },
          })
        },
      })
      .catch((err) => {
        document.set({ uploadProgress: null })
        throw err
      })

    // Reset upload progress after version has been created
    // and add the version.
    return runInAction(() => {
      document.set({ uploadProgress: null })
      return this.versions.set(result)
    })
  }

  async correctFields(
    documentId: Id,
    documentVersionId: Id,
    fields: CorrectDocumentFieldPayload,
  ): Promise<void> {
    await this.api.version.correctFields(
      Id.assert(documentId),
      Id.assert(documentVersionId),
      fields,
    )

    await this.refreshVersion(documentId, documentVersionId)
  }

  async refreshVersion(documentId: Id, documentVersionId: Id) {
    return this.api.version
      .get({
        document_id: Id.assert(documentId),
        version_id: Id.assert(documentVersionId),
      })
      .then((version) => this.versions.set(version))
  }

  async confirmDocument(documentId: Id, documentVersionId: Id, fileId: Id) {
    await this.api.clientDataVerification.confirm({
      document_id: Id.assert(documentId),
      version_id: Id.assert(documentVersionId),
      file_id: Id.assert(fileId),
    })

    await this.refreshVersion(documentId, documentVersionId)
  }
}
